import { FloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { floorAssemblyBaseOffset, floorAssemblyFloorPlateThickness } from "doc/elements/floor/floorAssembly";
import { pathPriorityOpposite } from "doc/utils/wallTools";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";

export function floorPathBaseOffset(
  path: Pick<FloorPath, "floorPriority" | "leftFloorAssembly" | "rightFloorAssembly">,
  priority?: PathPriority,
) {
  const { floorPriority, leftFloorAssembly, rightFloorAssembly } = path;
  const floorPathPriority = priority ?? floorPriority;

  const leftRoomFloorOffset = floorAssemblyBaseOffset(leftFloorAssembly);
  const rightRoomFloorOffset = floorAssemblyBaseOffset(rightFloorAssembly);

  if (floorPathPriority === PathPriority.NONE) {
    if (leftFloorAssembly.assemblyType !== rightFloorAssembly.assemblyType) {
      return leftFloorAssembly.assemblyType === FloorAssemblyType.VOID ? rightRoomFloorOffset : leftRoomFloorOffset;
    }
    return Math.max(leftRoomFloorOffset, rightRoomFloorOffset);
  }

  return floorPathPriority === PathPriority.LEFT ? leftRoomFloorOffset : rightRoomFloorOffset;
}

export function floorPathTop(
  level: Level,
  path?: Pick<FloorPath, "floorPriority" | "leftFloorAssembly" | "rightFloorAssembly">,
  priority?: PathPriority,
): WorldInches {
  return worldInchesNew(level.floorElevation + (path ? floorPathBaseOffset(path, priority) : 0));
}

export function floorPathBottom(
  level: Level,
  path: Pick<FloorPath, "floorPriority" | "leftFloorAssembly" | "rightFloorAssembly">,
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  priority?: PathPriority,
): WorldInches {
  return worldInchesNew(
    floorPathTop(level, path, priority) -
      floorPathFloorPlateThickness(path, framingComponents, slabComponents, sheathingComponents, priority),
  );
}

export function floorPathFloorPlateThickness(
  path: Pick<FloorPath, "floorPriority" | "leftFloorAssembly" | "rightFloorAssembly">,
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  priority?: PathPriority,
) {
  const { floorPriority, leftFloorAssembly, rightFloorAssembly } = path;
  const floorPathPriority = priority ?? floorPriority;

  const leftRoomFloorPlateThickness = floorAssemblyFloorPlateThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    leftFloorAssembly,
  );
  const rightRoomFloorPlateThickness = floorAssemblyFloorPlateThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    rightFloorAssembly,
  );

  if (floorPathPriority === PathPriority.NONE) {
    return Math.max(leftRoomFloorPlateThickness, rightRoomFloorPlateThickness);
  }

  return floorPathPriority === PathPriority.LEFT ? leftRoomFloorPlateThickness : rightRoomFloorPlateThickness;
}

export function floorPathOpposite({
  halfEdgeIndex,
  planarGraphId,
  leftWallRoom,
  rightWallRoom,
  leftRoom,
  rightRoom,
  leftRoomClass,
  rightRoomClass,
  leftFloorAssembly,
  rightFloorAssembly,
  floorOffset,
  floorPriority,
  leftWallOffset,
  rightWallOffset,
  doors,
  windows,
}: FloorPath): FloorPath {
  return {
    halfEdgeIndex: planarGraphAdjacentHalfEdge(halfEdgeIndex),
    planarGraphId,
    leftWallRoom: rightWallRoom,
    rightWallRoom: leftWallRoom,
    leftRoom: rightRoom,
    rightRoom: leftRoom,
    leftRoomClass: rightRoomClass,
    rightRoomClass: leftRoomClass,
    leftFloorAssembly: rightFloorAssembly,
    rightFloorAssembly: leftFloorAssembly,
    floorOffset: -floorOffset,
    floorPriority: pathPriorityOpposite(floorPriority),
    leftWallOffset: rightWallOffset,
    rightWallOffset: leftWallOffset,
    doors,
    windows,
  };
}
